import { Controller } from '@hotwired/stimulus'

class AppcuesLauncherController extends Controller {
  private static readonly APPCUES_CONTAINER_TAG = 'appcues-experience-container'
  private static readonly LAUNCHPAD_WRAPPER_ID = 'launchpad-button-wrapper'
  private static readonly LAUNCHPAD_BUTTON_ID = 'launchpad-button'
  private static readonly NL_CAMPUS_FLOW_ID =
    '54ec5e03-8d42-4e62-9f50-aee15e4e8e11'

  private bodyObserver: MutationObserver | null = null
  private shadowObservers: Map<HTMLElement, MutationObserver> = new Map()
  private observedContainers: Set<HTMLElement> = new Set()

  connect(): void {
    this.setupBodyObserver()
    this.checkAndSetupShadowObservers()
  }

  disconnect(): void {
    this.disconnectBodyObserver()
    this.disconnectAllShadowObservers()
    this.observedContainers.clear()
  }

  launch(): void {
    if (window.Silverfin?.currentFirmCountry === 'NL') {
      window.Appcues?.show(AppcuesLauncherController.NL_CAMPUS_FLOW_ID)
      return
    }

    for (const container of this.getAppcuesContainers()) {
      if (!container.shadowRoot) continue

      const launchpadWrapper = this.getLaunchpadWrapper(container)
      const launchpadButton = this.getLaunchpadButton(container)

      if (launchpadWrapper && launchpadButton) {
        this.showLaunchpad(launchpadWrapper)
        this.disconnectAllShadowObservers()
        launchpadButton.click()
        return
      }
    }
  }

  private setupBodyObserver(): void {
    this.bodyObserver = new MutationObserver(() => {
      this.checkAndSetupShadowObservers()
    })
    this.bodyObserver.observe(document.body, {
      childList: true,
      subtree: true,
    })
  }

  private checkAndSetupShadowObservers(): void {
    for (const container of this.getAppcuesContainers()) {
      if (container.shadowRoot && !this.observedContainers.has(container)) {
        this.observedContainers.add(container)
        this.setupShadowObserver(container)
      }
    }

    this.hideLaunchpad()
  }

  private setupShadowObserver(container: HTMLElement): void {
    const observer = new MutationObserver(() => this.hideLaunchpad())
    observer.observe(container.shadowRoot!, {
      childList: true,
      subtree: true,
    })
    this.shadowObservers.set(container, observer)
  }

  private hideLaunchpad(): void {
    for (const container of this.getAppcuesContainers()) {
      if (!container.shadowRoot) continue

      const launchpadWrapper = this.getLaunchpadWrapper(container)
      if (launchpadWrapper && launchpadWrapper.style.display !== 'none') {
        launchpadWrapper.style.display = 'none'
      }
    }
  }

  private showLaunchpad(launchpadWrapper: HTMLElement): void {
    launchpadWrapper.style.display = ''
  }

  private disconnectBodyObserver(): void {
    if (this.bodyObserver) {
      this.bodyObserver.disconnect()
      this.bodyObserver = null
    }
  }

  private disconnectAllShadowObservers(): void {
    for (const observer of this.shadowObservers.values()) {
      observer.disconnect()
    }
    this.shadowObservers.clear()
    this.observedContainers.clear()
  }

  private getAppcuesContainers(): HTMLElement[] {
    return Array.from(
      document.getElementsByTagName(
        AppcuesLauncherController.APPCUES_CONTAINER_TAG
      )
    ) as HTMLElement[]
  }

  private getLaunchpadWrapper(container: HTMLElement): HTMLElement | null {
    return container.shadowRoot?.getElementById(
      AppcuesLauncherController.LAUNCHPAD_WRAPPER_ID
    ) as HTMLElement | null
  }

  private getLaunchpadButton(container: HTMLElement): HTMLElement | null {
    return container.shadowRoot?.getElementById(
      AppcuesLauncherController.LAUNCHPAD_BUTTON_ID
    ) as HTMLElement | null
  }
}

export default AppcuesLauncherController
